/**
 * iCalendar (.ics) builder for meetings. Times are written in UTC, text is escaped per RFC 5545 and long
 * lines are folded at 75 octets, so the file opens in Outlook, Google and Apple Calendar.
 */

const pad = (n, width = 2) => String(n).padStart(width, '0')

/** 2026-10-12T14:30:00Z as 20261012T143000Z. @param {Date} date */
export const formatIcsUtc = date =>
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`

/** Escape a TEXT value: backslash, semicolon, comma and line breaks. @param {unknown} value */
export const escapeIcsText = value =>
    String(value ?? '')
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\r\n|\r|\n/g, '\\n')

const utf8Length = codePoint => {
    if (codePoint < 0x80) return 1
    if (codePoint < 0x800) return 2
    if (codePoint < 0x10000) return 3
    return 4
}

/**
 * Fold a content line to at most 75 octets per physical line (continuation lines start with one space),
 * never splitting a multi-byte character.
 * @param {string} line
 * @returns {string}
 */
export const foldLine = line => {
    const parts = []
    let current = ''
    let bytes = 0
    let limit = 75
    Array.from(line).forEach(char => {
        const size = utf8Length(char.codePointAt(0))
        if (bytes + size > limit) {
            parts.push(current)
            current = ''
            bytes = 0
            limit = 74 // the leading space of a continuation line counts too
        }
        current += char
        bytes += size
    })
    parts.push(current)
    return parts.join('\r\n ')
}

// Keep header-style values on one line: strip anything that could end the line or inject a property.
const cleanParam = value => String(value ?? '').replace(/[\r\n";:,]/g, ' ').trim()
const SIMPLE_EMAIL = /^[^\s@;,<>"]+@[^\s@;,<>"]+\.[^\s@;,<>"]+$/

/**
 * @param {{
 *   uid: string,
 *   title: string,
 *   start: Date,
 *   durationMinutes?: number,
 *   description?: string,
 *   location?: string,
 *   attendee?: { name?: string, email?: string },
 *   now?: Date,
 * }} meeting
 * @returns {string} calendar text with CRLF line endings
 */
export const buildIcs = ({ uid, title, start, durationMinutes = 30, description = '', location = '', attendee, now = new Date() }) => {
    const end = new Date(start.getTime() + Math.max(1, durationMinutes) * 60000)
    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//eAuditor//CRM//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        `UID:${cleanParam(uid)}`,
        `DTSTAMP:${formatIcsUtc(now)}`,
        `DTSTART:${formatIcsUtc(start)}`,
        `DTEND:${formatIcsUtc(end)}`,
        `SUMMARY:${escapeIcsText(title)}`,
    ]
    if (description) lines.push(`DESCRIPTION:${escapeIcsText(description)}`)
    if (location) lines.push(`LOCATION:${escapeIcsText(location)}`)
    if (attendee?.email && SIMPLE_EMAIL.test(attendee.email)) {
        const name = cleanParam(attendee.name)
        lines.push(`ATTENDEE;${name ? `CN="${name}";` : ''}RSVP=TRUE:mailto:${attendee.email}`)
    }
    lines.push('END:VEVENT', 'END:VCALENDAR')
    return `${lines.map(foldLine).join('\r\n')}\r\n`
}

/** A file-safe name such as "meeting-ana-gomez-2026-10-12.ics". */
export const icsFilename = (title, start) => {
    const slug = String(title || 'meeting')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40)
    return `${slug || 'meeting'}-${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}.ics`
}
